(() => {
  if (window.PoemeGiftProgress) return;

  const gifts = state => state.items.filter(item => String(item.productId) === String(state.giftProductId));
  const evaluate = state => {
    const lines = gifts(state);
    const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
    const eligible = state.supported && state.active && state.subtotal >= state.threshold && state.otherDiscount === 0;
    const free = eligible && quantity === 1 && lines.length === 1 && String(lines[0].variantId) === String(state.giftVariantId) && lines[0].finalPrice === 0 && lines[0].campaignApplied;
    return { lines, quantity, eligible, free, remaining: Math.max(0, state.threshold - state.subtotal), progress: Math.min(100, Math.max(0, state.subtotal / state.threshold * 100)) };
  };

  const controller = {
    state: null, texts: null, busy: false, changing: 0, stale: true, feedback: '', epoch: 0, pending: null, timer: null, expiry: null,
    cards() { return [...document.querySelectorAll('poeme-gift-progress')]; },
    root() { return window.Shopify?.routes?.root || '/'; },
    money(value) {
      return new Intl.NumberFormat(this.state?.locale || 'fr', { style: 'currency', currency: 'EUR', maximumFractionDigits: value % 100 === 0 ? 0 : 2 }).format(value / 100);
    },
    initialize(card) {
      const textNode = card.querySelector('[data-poeme-gift-translations]');
      if (!this.texts && textNode) this.texts = JSON.parse(textNode.textContent);
      const data = card.querySelector('[data-poeme-gift-state]');
      if (!this.state && data) this.state = JSON.parse(data.textContent);
      this.render();
      this.schedule();
    },
    render() {
      if (!this.state || !this.texts) return;
      const s = this.state, status = evaluate(s), loading = this.busy || this.changing > 0 || this.stale;
      for (const card of this.cards()) {
        card.hidden = !s.supported && status.quantity === 0;
        card.setAttribute('aria-busy', String(loading));
        card.classList.toggle('poeme-gift--added', status.free);
        const message = card.querySelector('[data-gift-message]');
        message.textContent = !s.active ? this.texts.expired : status.remaining > 0 ? this.texts.remaining.replace('__AMOUNT__', this.money(status.remaining)) : status.free || status.eligible ? this.texts.available : this.texts.conflict;
        const progress = card.querySelector('[data-gift-progress]');
        progress.value = status.progress;
        progress.textContent = `${Math.round(status.progress)}%`;
        card.querySelector('[data-gift-value]').textContent = this.texts.value.replace('__AMOUNT__', this.money(s.giftPrice));
        const button = card.querySelector('[data-gift-claim]');
        button.hidden = !status.free && (!status.eligible || !s.available);
        button.disabled = loading || status.free || !status.eligible || !s.available;
        button.textContent = status.free ? this.texts.added : loading ? this.texts.checking : this.texts.claim;
        const note = card.querySelector('[data-gift-feedback]');
        const code = this.feedback || (!s.available && !status.free ? 'sold_out' : '');
        note.hidden = !code;
        note.textContent = this.texts[code] || '';
      }
      const protect = this.busy || this.changing > 0 || (status.quantity > 0 && (this.stale || !status.free));
      document.documentElement.classList.toggle('poeme-gift-checkout-locked', protect);
      document.documentElement.classList.toggle('poeme-gift-present', status.quantity > 0);
      for (const button of document.querySelectorAll('button[name="checkout"]')) {
        if (protect && !button.disabled) {
          button.setAttribute('data-poeme-gift-blocked', ''); button.disabled = true;
        } else if (!protect && button.hasAttribute('data-poeme-gift-blocked')) {
          button.removeAttribute('data-poeme-gift-blocked'); button.disabled = false;
        }
      }
    },
    async read() {
      const url = `${this.root()}?sections=gift-progress-state&_gift=${Date.now()}`;
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000), headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('state_request_failed');
      const sections = await response.json();
      const html = sections['gift-progress-state'];
      if (typeof html !== 'string') throw new Error('state_missing');
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-poeme-gift-state]');
      const state = JSON.parse(node?.textContent || 'null');
      if (!state || state.version !== 3 || !Array.isArray(state.items) || state.threshold !== 30000 || !Number.isFinite(state.subtotal) || String(state.giftVariantId) !== '57559809818948' || String(state.giftProductId) !== '15746986705220') throw new Error('state_invalid');
      return state;
    },
    accept(state) {
      this.state = state;
      this.stale = false;
      clearTimeout(this.expiry);
      if (state.active) this.expiry = setTimeout(() => { this.stale = true; this.schedule(); }, Math.min(2147483647, Math.max(1000, (state.endsAt - state.serverTime) * 1000)));
    },
    sectionIds() {
      const page = document.getElementById('main-cart-items')?.dataset.id;
      return [...new Set(['cart-drawer', 'cart-icon-bubble', page].filter(Boolean))];
    },
    async mutate(endpoint, body) {
      const response = await fetch(`${this.root()}cart/${endpoint}.js`, {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(12000), headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error('cart_request_failed');
      this.paintSections(data.sections);
      return data;
    },
    paintSections(sections = {}) {
      for (const [id, html] of Object.entries(sections || {})) {
        if (typeof html !== 'string') continue;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-drawer') {
          const drawer = document.querySelector('cart-drawer'), replacement = parsed.querySelector('cart-drawer');
          if (drawer && replacement) {
            drawer.innerHTML = replacement.innerHTML;
            drawer.classList.toggle('is-empty', replacement.classList.contains('is-empty'));
            if (typeof drawer.enableOverlayClick === 'function') drawer.enableOverlayClick();
          }
        } else {
          const target = document.getElementById(`shopify-section-${id}`) || document.getElementById(id);
          const replacement = parsed.querySelector('.shopify-section');
          if (target && replacement) target.innerHTML = replacement.innerHTML;
        }
      }
      if (typeof window.initUpsellSlider === 'function') window.initUpsellSlider();
      document.dispatchEvent(new CustomEvent('poeme:gift-cart-updated'));
    },
    async remove(state) {
      const updates = Object.fromEntries(gifts(state).map(line => [line.key, 0]));
      if (Object.keys(updates).length) await this.mutate('update', { updates });
    },
    async reconcile(state) {
      const status = evaluate(state);
      if (!status.quantity) return state;
      if (!status.eligible) {
        await this.remove(state);
        this.feedback = 'removed';
        return this.read();
      }
      if (status.quantity > 1 || status.lines.length > 1 || String(status.lines[0].variantId) !== String(state.giftVariantId)) {
        let kept = false;
        const updates = {};
        for (const line of status.lines) {
          const keep = !kept && String(line.variantId) === String(state.giftVariantId);
          updates[line.key] = keep ? 1 : 0;
          kept ||= keep;
        }
        await this.mutate('update', { updates });
        state = await this.read();
      }
      if (evaluate(state).quantity && !evaluate(state).free) {
        await this.remove(state);
        this.feedback = 'not_free';
        state = await this.read();
      }
      return state;
    },
    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 60);
    },
    async refresh() {
      if (this.busy || this.changing > 0) return;
      if (this.pending) return this.pending;
      const epoch = this.epoch;
      this.stale = true;
      this.render();
      this.pending = (async () => {
        try {
          let state = await this.read();
          if (epoch !== this.epoch || this.changing > 0 || this.busy) return;
          this.state = state;
          this.render();
          this.busy = true;
          state = await this.reconcile(state);
          if (epoch === this.epoch && this.changing === 0) this.accept(state);
        } catch {
          this.feedback = 'error';
          this.stale = true;
        } finally {
          this.busy = false;
          this.pending = null;
          this.render();
          if (epoch !== this.epoch && this.changing === 0) this.schedule();
        }
      })();
      return this.pending;
    },
    async claim() {
      if (this.busy || this.changing > 0 || this.stale || !this.state || !evaluate(this.state).eligible || !this.state.available) return;
      this.busy = true;
      this.feedback = '';
      this.render();
      const work = async () => {
        let state = await this.read();
        if (this.changing > 0 || !evaluate(state).eligible || !state.available) { this.accept(state); return; }
        if (evaluate(state).quantity === 0) await this.mutate('add', { items: [{ id: state.giftVariantId, quantity: 1, properties: { _poeme_gift: 'cadeau_300' } }] });
        state = await this.reconcile(await this.read());
        if (!evaluate(state).free) this.feedback ||= 'not_free';
        this.accept(state);
      };
      try {
        if (navigator.locks?.request) await navigator.locks.request('poeme-gift-cadeau-300', work);
        else await work();
      } catch {
        this.feedback = 'error';
        this.stale = true;
        try { const state = await this.read(); await this.remove(state); this.accept(await this.read()); } catch { this.stale = true; }
      } finally {
        this.busy = false;
        this.render();
        this.schedule();
      }
    },
    async checkout(event, trigger) {
      if (!this.state) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || this.changing > 0) return;
      await this.refresh();
      if (this.stale || this.busy || this.changing > 0 || (evaluate(this.state).quantity && !evaluate(this.state).free)) {
        this.feedback = 'checkout_blocked'; this.render(); return;
      }
      const previousForm = trigger.form || (event.type === 'submit' ? event.target : null);
      const form = previousForm?.id ? document.getElementById(previousForm.id) : previousForm?.isConnected ? previousForm : null;
      if (form) {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; input.value = 'checkout'; form.append(input);
        HTMLFormElement.prototype.submit.call(form);
      } else location.assign(`${this.root()}checkout`);
    }
  };
  window.PoemeGiftProgress = { evaluate, controller };
  customElements.define('poeme-gift-progress', class extends HTMLElement {
    connectedCallback() { controller.initialize(this); }
  });
  document.addEventListener('click', event => {
    const claim = event.target.closest('[data-gift-claim]');
    if (claim) { event.preventDefault(); controller.claim(); return; }
    const checkout = event.target.closest('[name="checkout"], a[href*="/checkout"]');
    if (checkout) controller.checkout(event, checkout);
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') controller.checkout(event, event.submitter);
  }, true);
  document.addEventListener('poeme:cart-changing', () => { controller.changing++; controller.epoch++; controller.stale = true; controller.render(); });
  document.addEventListener('poeme:cart-settled', () => { controller.changing = Math.max(0, controller.changing - 1); controller.stale = true; controller.schedule(); });
  document.addEventListener('cart:updated', () => { controller.stale = true; controller.schedule(); });
  window.addEventListener('focus', () => { controller.stale = true; controller.schedule(); });
  window.addEventListener('pageshow', () => { controller.stale = true; controller.schedule(); });
})();
